import React from "react";
import { AvatarChip } from "../AvatarChip";

/**
 * InboxBlock — dashboard inbox preview with INLINE accept/decline so
 * users can act without opening the full inbox modal.
 */
export function InboxBlock({ threads, invites, inboxCount, inviteBusyId, onDecide, onOpenDm, onOpenInbox }) {
  return (
    <div className="glass rounded-3xl p-4" data-testid="dash-inbox-side">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-envelope mr-1" /> INBOX
        </p>
        <span className="text-[10px] text-white/40">{inboxCount} pending</span>
      </div>

      <div className="mt-3 space-y-2">
        {/* Pending invites first — actionable. */}
        {invites.slice(0, 4).map((m) => (
          <InviteRow key={m.id} item={m} busy={inviteBusyId === m.id} onDecide={onDecide} onOpenInbox={onOpenInbox} />
        ))}

        {threads.slice(0, 4).map((t) => (
          <button key={t.pair_key} type="button"
                  onClick={() => onOpenDm(t)}
                  data-testid={`dash-thread-${t.other.user_id}`}
                  className={`w-full text-left flex items-center gap-2 p-2 rounded-xl bg-black/30 hover:bg-white/5 ${t.unread_count > 0 ? "border border-[#14b8a6]/40" : ""}`}>
            <AvatarChip avatarId={t.other.avatar_id} pfpUrl={t.other.pfp_url} size={32} />
            <div className="flex-1 min-w-0">
              <p className={`text-xs truncate ${t.unread_count > 0 ? "font-bold text-white" : "text-white/80"}`}>
                {t.other.name}
              </p>
              <p className={`text-[10px] truncate ${t.unread_count > 0 ? "text-[#14b8a6]" : "text-white/45"}`}>
                {t.last_from_me && <span className="text-white/30 mr-1">You:</span>}{t.last_message}
              </p>
            </div>
            {t.unread_count > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center">
                {t.unread_count > 9 ? "9+" : t.unread_count}
              </span>
            )}
          </button>
        ))}

        {threads.length === 0 && invites.length === 0 && (
          <p className="text-[11px] text-white/40 text-center py-3">No threads or invites yet.</p>
        )}
      </div>

      <button type="button" onClick={onOpenInbox} data-testid="dash-open-inbox"
              className="mt-3 w-full text-[10px] tracking-widest py-2 rounded-xl btn-ghost">
        OPEN INBOX <i className="fas fa-arrow-right ml-1" />
      </button>
    </div>
  );
}

/** Single pending invite row with inline ACCEPT/DECLINE buttons. */
function InviteRow({ item, busy, onDecide, onOpenInbox }) {
  const p = item.payload || {};
  const meta = (() => {
    switch (item.kind) {
      case "league_join_invite":  return { icon: "fa-trophy",         color: "text-[#14b8a6]", title: p.league_name || "League invite",      sub: p.from_user_name ? `from ${p.from_user_name}` : "Tap ✓ to join." };
      case "team_join_invite":    return { icon: "fa-shield-halved",  color: "text-[#14b8a6]", title: p.team_name || "Team invite",          sub: `Join ${p.team_name || "the team"}.` };
      case "team_join_request":   return { icon: "fa-user-plus",      color: "text-[#14b8a6]", title: `${p.requester_name || "Player"} → ${p.team_name || "team"}`, sub: "Wants to join your team." };
      case "team_challenge_invite": return { icon: "fa-bolt",         color: "text-[#14b8a6]", title: `${p.challenger_team_name || "Squad"} → ${p.target_team_name || "you"}`, sub: p.scheduled_time ? new Date(p.scheduled_time).toLocaleString() : "Challenge request." };
      case "friend_request":      return { icon: "fa-user-group",     color: "text-[#14b8a6]", title: p.from_user_name || "Friend request", sub: "Friend request." };
      case "player_report":       return { icon: "fa-flag",           color: "text-[#14b8a6]", title: `Report: ${p.reported_name || "player"}`, sub: p.reason_label || "Open in INBOX to act." };
      default:                    return { icon: "fa-envelope",       color: "text-[#14b8a6]", title: p.title || "New item",                sub: "Pending action." };
    }
  })();

  return (
    <div data-testid={`dash-invite-${item.id}`}
         className="flex items-center gap-2 p-2 rounded-xl bg-[#14b8a6]/10 border border-[#14b8a6]/40">
      <div className={`w-8 h-8 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center ${meta.color}`}>
        <i className={`fas ${meta.icon}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-white truncate">{meta.title}</p>
        <p className="text-[9px] text-white/55 truncate">{meta.sub}</p>
      </div>
      {item.kind === "player_report" ? (
        <button
          type="button"
          onClick={() => onOpenInbox?.()}
          data-testid={`dash-invite-open-${item.id}`}
          title="Open the inbox to ban / allow / message — opens directly to the report row."
          className="px-2 py-1 rounded-md text-[10px] tracking-widest font-bold bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25"
        >
          REVIEW
        </button>
      ) : (
        <>
          <button type="button" onClick={() => onDecide(item, "accept")} disabled={busy}
                  data-testid={`dash-invite-accept-${item.id}`}
                  title="Approve"
                  className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center">
            <i className="fas fa-check text-[10px]" />
          </button>
          <button type="button" onClick={() => onDecide(item, "decline")} disabled={busy}
                  data-testid={`dash-invite-decline-${item.id}`}
                  title="Decline"
                  className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center">
            <i className="fas fa-xmark text-[10px]" />
          </button>
        </>
      )}
    </div>
  );
}
